import { type ReactNode, useEffect, useRef } from 'react';

import { cn } from '../lib/utils';
import { DataTableHeaderCell } from './data-table-header-cell';
import { DataTableLoadMore } from './data-table-load-more';
import type { DataTableColumn } from './data-table-types';
import { EmptyState } from './empty-state';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Table, TableBody, TableCell, TableHeader, TableRow } from './ui/table';

export type {
  DataTableColumn,
  DataTableColumnFilter,
  DataTableColumnSort,
  SortDirection,
} from './data-table-types';

interface DataTableProps<TRow> {
  columns: DataTableColumn<TRow>[];
  rows: TRow[];
  rowKey: (row: TRow) => string;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  footer?: ReactNode;
  /**
   * Opt-in: the table becomes a bounded-height flex column that scrolls
   * inside its own box, both axes, with the header pinned to the top —
   * instead of growing forever and leaving the page (really `<main>`) to
   * scroll. Needs an unbroken `flex flex-col` chain from the page's root
   * down to this component, since the bound comes from `flex-1 min-h-0`,
   * not a fixed height. Off by default so every other consumer is unchanged.
   */
  fillHeight?: boolean;
  /**
   * Opt-in: load on scroll. When the last row comes into view and `hasMore`,
   * `onReachEnd` is called once per page; a final row reads "Loading more…"
   * while `loadingMore`, and "That's everyone" once nothing is left.
   */
  onReachEnd?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  /** What the last row says once nothing is left; names what the rows are. */
  endText?: string;
  /** When this changes (a new filter or sort), the scroll area returns to the top. */
  scrollResetKey?: string;
  /**
   * Opt-in: a row opens something beside the table. A click on a link or a
   * button inside the row does that control's own job and never reaches this.
   */
  onRowClick?: (row: TRow) => void;
  /** The row whose `rowKey` this is reads as selected. */
  selectedRowKey?: string | null;
}

/** Whatever inside a row has its own job, including a menu item portalled out of the row. */
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="menuitem"]';

/** Placeholder rows are positional and never reorder, so a stable id list is enough. */
const SKELETON_ROWS = ['a', 'b', 'c', 'd', 'e'];

export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyAction,
  footer,
  fillHeight = false,
  onReachEnd,
  hasMore = false,
  loadingMore = false,
  endText = "That's everyone",
  scrollResetKey,
  onRowClick,
  selectedRowKey = null,
}: DataTableProps<TRow>) {
  const scrollArea = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The key is the trigger, not a value read here.
    void scrollResetKey;
    scrollArea.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);

  const isEmpty = !loading && rows.length === 0;
  // A table whose headers carry filters keeps them when nothing matches: the
  // way out of an empty result is the control that produced it.
  const hasWidths = columns.some((column) => column.width);
  const keepsHeader = columns.some((column) => column.filter || column.sort);

  if (isEmpty && !keepsHeader) {
    return (
      <Card className={cn('py-0', fillHeight && 'min-h-0 flex-1 items-center justify-center')}>
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      </Card>
    );
  }

  return (
    <Card className={cn('gap-0 overflow-hidden py-0', fillHeight && 'min-h-0 flex-1')}>
      <Table
        containerRef={scrollArea}
        containerClassName={fillHeight ? 'min-h-0 flex-1 overflow-auto' : undefined}
        className={cn(hasWidths && 'table-fixed')}
      >
        {hasWidths ? (
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} className={cn(column.width, column.className)} />
            ))}
          </colgroup>
        ) : null}
        <TableHeader sticky={fillHeight}>
          <TableRow className="border-border bg-muted hover:bg-muted">
            {columns.map((column) => (
              <DataTableHeaderCell key={column.key} column={column} />
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {/* Skeleton rows keep the table from collapsing and reflowing on load. */}
          {isEmpty ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length}>
                <EmptyState
                  title={emptyTitle}
                  description={emptyDescription}
                  action={emptyAction}
                />
              </TableCell>
            </TableRow>
          ) : null}

          {loading
            ? SKELETON_ROWS.map((placeholder) => (
                <TableRow key={`skeleton-${placeholder}`}>
                  {columns.map((column) => (
                    <TableCell key={column.key} className={column.className}>
                      <Skeleton className="h-4 w-full max-w-40" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row) => (
                <TableRow
                  key={rowKey(row)}
                  data-state={selectedRowKey === rowKey(row) ? 'selected' : undefined}
                  className={cn(onRowClick && 'cursor-pointer')}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={
                    onRowClick
                      ? (event) => {
                          const target = event.target as HTMLElement;
                          if (target.closest(INTERACTIVE_SELECTOR)) return;

                          onRowClick(row);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' && event.target === event.currentTarget) {
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        column.align === 'right' && 'text-right',
                        column.align === 'center' && 'text-center',
                        column.className,
                      )}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

          {onReachEnd && !loading && !isEmpty ? (
            <DataTableLoadMore
              colSpan={columns.length}
              rowCount={rows.length}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onReachEnd={onReachEnd}
              endText={endText}
            />
          ) : null}
        </TableBody>
      </Table>

      {footer}
    </Card>
  );
}
